import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Input,
  Stack,
  Heading,
  Flex,
  Text,
  Icon,
  InputGroup,
  InputLeftElement,
  Select,
  Alert,
  AlertIcon,
  useToast,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { Navigate } from 'react-router-dom';
import { FiUser, FiLock, FiDatabase } from 'react-icons/fi';

function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

const LoginForm = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [instanceId, setInstanceId] = useState('');
  const [instances, setInstances] = useState([]);
  const [loadingInstances, setLoadingInstances] = useState(true);
  const [loggedIn, setLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const toast = useToast();

  // Fetch available instances on mount
  useEffect(() => {
    const fetchInstances = async () => {
      try {
        const response = await fetch('/api/v1/instances');
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.instances) {
            setInstances(data.instances);
            if (data.instances.length === 1) {
              setInstanceId(data.instances[0].id.toString());
            }
          }
        }
      } catch (e) {
        console.error('Failed to fetch instances:', e);
      } finally {
        setLoadingInstances(false);
      }
    };
    fetchInstances();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    const csrftoken = getCookie('csrftoken');
    
    try {
      const response = await fetch('/api/v1/admin-ui/auth/login/', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-CSRFToken': csrftoken,
        },
        body: JSON.stringify({ 
          username, 
          password,
          instance_id: instanceId 
        }),
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('instance_id', instanceId);
        localStorage.setItem('instance_name', data.instance_name || '');
        localStorage.setItem('user_name', data.user_name || username);
        onLogin();
        setLoggedIn(true);
      } else {
        setError(data.message || 'Login failed. Only super administrators can access this panel.');
        toast({
          title: 'Login failed',
          description: data.message || 'Only super administrators can access this panel.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (e) {
      setError('An error occurred during login.');
      toast({
        title: 'Login failed',
        description: 'An error occurred.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (loggedIn) {
    return <Navigate to="/" />;
  }

  return (
    <Flex
      minHeight="100vh"
      align="center"
      justify="center"
      bgGradient="linear(to-br, orange.500, amber.700, orange.900)"
    >
      <Box
        bg="white"
        px={10}
        py={12}
        borderRadius="2xl"
        boxShadow="2xl"
        width="100%"
        maxWidth="420px"
        mx={4}
      >
        <Stack as="form" spacing={6} onSubmit={handleLogin}>
          {/* Logo */}
          <Flex direction="column" align="center" mb={4}>
            <Box
              w={20}
              h={20}
              borderRadius="full"
              overflow="hidden"
              bg="orange.500"
              p={1}
              mb={4}
              boxShadow="lg"
            >
              <img src="/static/selwyn-logo.png" alt="Selwyn" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
            </Box>
            <Heading size="lg" color="gray.800">API Admin</Heading>
            <Text color="gray.500" fontSize="sm" mt={1}>Super Administrator Access Only</Text>
          </Flex>

          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          )}

          <FormControl isRequired>
            <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">Instance</FormLabel>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon as={FiDatabase} color="gray.400" />
              </InputLeftElement>
              <Select
                placeholder={loadingInstances ? "Loading instances..." : "Select an instance"}
                value={instanceId}
                onChange={(e) => setInstanceId(e.target.value)}
                size="lg"
                borderRadius="lg"
                focusBorderColor="orange.500"
                pl={10}
                isDisabled={loadingInstances}
              >
                {instances.map((instance) => (
                  <option key={instance.id} value={instance.id}>
                    {instance.name} {instance.slug ? `(${instance.slug})` : ''}
                  </option>
                ))}
              </Select>
            </InputGroup>
          </FormControl>

          <FormControl isRequired>
            <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">Username</FormLabel>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon as={FiUser} color="gray.400" />
              </InputLeftElement>
              <Input
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                size="lg"
                borderRadius="lg"
                focusBorderColor="orange.500"
              />
            </InputGroup>
          </FormControl>

          <FormControl isRequired>
            <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">Password</FormLabel>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon as={FiLock} color="gray.400" />
              </InputLeftElement>
              <Input
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                size="lg"
                borderRadius="lg"
                focusBorderColor="orange.500"
              />
            </InputGroup>
          </FormControl>

          <Button
            type="submit"
            size="lg"
            bgGradient="linear(to-r, orange.500, orange.600)"
            color="white"
            _hover={{
              bgGradient: "linear(to-r, orange.600, orange.700)",
              transform: "translateY(-2px)",
              boxShadow: "lg",
            }}
            _active={{
              transform: "translateY(0)",
            }}
            borderRadius="lg"
            isLoading={isLoading}
            loadingText="Signing in..."
            transition="all 0.2s"
            isDisabled={!instanceId}
          >
            Sign In
          </Button>
        </Stack>
      </Box>
    </Flex>
  );
};

export default LoginForm;
